/**
 * Knast-Seite – wird bei ARRESTED WS-Event oder direkter Navigation zu /jail gerendert.
 * Alle anderen Tabs ausser /jail und /profile sind gesperrt wenn isInJail = true.
 * Timer laeuft clientseitig, alle 30s Sync mit /api/v1/jail/status.
 *
 * Layout: Dashboard-Shell mit ausgegrauter Sidebar (pointer-events: none).
 */

import '../styles/dashboard.css';
import { api } from '../services/api.js';
import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import { refreshBalance } from '../utils/refreshBalance.js';
import { calcNoticeDisplayMs } from '../utils/notice-duration.js';

// ─── Typen ──────────────────────────────────────────────────────────────────

interface JailJobDef {
  id: string;
  name: string;
  durationMinutes: number;
  type: 'JOB';
  rewardEurCents?: string;
  rewardEurCentsMin?: string;
  rewardEurCentsMax?: string;
  rewardXpPerMinute?: number;
}

interface JailActivityDef {
  id: string;
  name: string;
  cooldownHours: number;
  type: 'ACTIVITY';
  rewardEurCents?: string;
  rewardBlackMoneyCents?: string;
  rewardWantedPointsRemoved?: number;
  rewardContact?: boolean;
  rewardGeruechteFlag?: boolean;
  riskFailChance?: number;
  riskExtraJailMinutes?: number;
}

interface ActiveJailJob {
  id: string;
  type: string;
  startedAt: string;
  completesAt: string;
  progressPercent: number;
}

interface JailReportJob {
  type: string;
  name: string;
  reward: { eurCents?: string; xp?: number };
}

interface JailReportActivity {
  type: string;
  name: string;
  success: boolean;
  outcome: { eurCents?: string; wantedPointsRemoved?: number; contact?: string | null };
}

interface JailReport {
  completedJobs: JailReportJob[];
  completedActivities: JailReportActivity[];
  totalNoxCents: string;
  totalXp: number;
  contactsUnlocked: string[];
}

interface JailStatus {
  isInJail: boolean;
  remainingMinutes: number;
  jailReleasesAt: string | null;
  jailReport?: JailReport;
  activeJob: ActiveJailJob | null;
  availableJobs: JailJobDef[];
  availableActivities: JailActivityDef[];
  cooldowns: Record<string, string>;
}

interface SmuggleJobView {
  id: string;
  itemKey: string;
  itemLabel: string;
  quantity: number;
  riskLevel: number;
  flPenalty: number;
  rewardCents: string;
  isTrustTest: boolean;
  acceptedAt: string | null;
  completesAt: string | null;
  isCompleted: boolean;
  isFailed: boolean;
  expiresAt: string;
}

interface CellMateView {
  id: string;
  npcName: string;
  remainingDays: number;
  trustLevel: number;
  expiresAt: string;
  isActive: boolean;
  activeJob: SmuggleJobView | null;
}

// ─── Modularer State ────────────────────────────────────────────────────────

let statusPollInterval: ReturnType<typeof setInterval> | null = null;
let jailCountdownInterval: ReturnType<typeof setInterval> | null = null;
let jailJobCountdownInterval: ReturnType<typeof setInterval> | null = null;
let currentStatus: JailStatus | null = null;
let currentCellMate: CellMateView | null = null;
let jailReleaseTime: Date | null = null;
let jobCompleteTime: Date | null = null;

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function formatCents(centStr: string): string {
  const val = parseFloat(centStr) / 100;
  return val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─── Haupteinstiegspunkt ─────────────────────────────────────────────────────

export async function renderJailPage(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  stopAllTimers();
  app.innerHTML = '<div class="jail-loading">LADE KNAST-DATEN...</div>';

  injectStyles();

  let status: JailStatus;
  try {
    const res = await api.get<{ data: JailStatus }>('/jail/status');
    // Doppel-Cast bewusst: der api-Client entpackt eine {data,meta}-Hülle nur, wenn der Backend-
    // Response tatsächlich so geformt ist (hasDataEnvelope() in services/api.ts) - res kann zur
    // Laufzeit bereits das flache Payload sein, auch wenn der generische Typ hier {data:...}
    // behauptet. Gilt für alle Vorkommen dieses Musters in dieser Datei.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    status = (res as unknown as { data: JailStatus }).data ?? (res as unknown as JailStatus);
  } catch {
    app.innerHTML = '<div class="jail-error">FEHLER BEIM LADEN DES KNAST-STATUS.</div>';
    return;
  }

  // Spieler nicht im Knast → zurück zum Dashboard
  if (!status.isInJail) {
    navigate('/dashboard');
    return;
  }

  // Zellengenosse laden (separat, Fehler ignorieren)
  try {
    const cmRes = await api.get<{ data: CellMateView | null }>('/jail/cellmate');
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- s.o.
    currentCellMate = (cmRes as unknown as { data: CellMateView | null }).data ?? null;
  } catch {
    currentCellMate = null;
  }

  currentStatus = status;
  renderJail(app, status);
  startTimers(status);
}

// ─── Render ─────────────────────────────────────────────────────────────────

function renderJail(container: HTMLElement, status: JailStatus): void {
  const player = gameState.getPlayer();
  const scheine = player?.scheine ?? 0;

  // Knast nutzt die Dashboard-Shell (Header + ausgegraute Sidebar + Content)
  // Die linke Nav ist deaktiviert – Spieler kann nichts anklicken.
  container.innerHTML = `
    <div class="dashboard">
      <header class="dash-header">
        <span class="dash-brand">&#9654; NIGHTTRADE</span>
        <div class="dash-economy-block">
          <span class="val-gold" title="Bargeld">${formatCents(player?.noxBalance ?? '0')}&nbsp;&#8358;</span>
          <div class="dash-eco-row2">
            <span class="val-bm" title="Schwarzgeld">${formatCents(player?.blackMoney ?? '0')}&nbsp;S-&#8358;</span>
            <span class="dash-economy-sep">|</span>
            <span class="dash-scheine-label">SCHEINE</span>
            <span class="val-scheine">${String(scheine)}</span>
          </div>
        </div>
      </header>

      <div class="dash-body">
        <!-- Linke Nav – ausgegraut, gesperrt -->
        <nav class="dash-nav jail-nav-locked" aria-disabled="true" aria-label="Navigation gesperrt">
          ${renderLockedNav()}
        </nav>

        <!-- Haupt-Content: Knast-Inhalt zentriert -->
        <main class="dash-main jail-main" id="jail-main-area">
          <div class="jail-page">
            <div class="jail-header">
              <span class="jail-title">GEFÄNGNIS</span>
              <span class="jail-timer ${status.remainingMinutes <= 5 ? 'jail-timer--danger' : ''}" id="jail-main-timer">
                ${formatMinutes(status.remainingMinutes)}
              </span>
            </div>

            <div class="jail-status-bar">
              <p class="jail-flavour">Du sitzt ein. Mach das Beste draus.</p>
              <div class="jail-actions-top">
                <button class="jail-btn jail-btn--bail" id="jail-btn-bail" type="button">
                  KAUTION – ${String(Math.ceil(status.remainingMinutes / 10) * 5)} SCHEINE
                </button>
                <span class="jail-scheine-hint">(${String(scheine)} Scheine verfügbar)</span>
              </div>
            </div>

            <div class="jail-section">
              <div class="jail-section-title">KNAST-AKTIVITÄTEN</div>
              ${renderJobsSection(status)}
            </div>

            <div class="jail-section">
              <div class="jail-section-title">EINMALIGE AKTIONEN (COOLDOWN)</div>
              ${renderActivitiesSection(status)}
            </div>

            <div class="jail-section" id="jail-cellmate-section">
              <div class="jail-section-title">ZELLENGENOSSE</div>
              ${renderCellmateSection()}
            </div>
          </div>
        </main>
      </div>
    </div>
  `;

  bindJailEvents(status);
}

function renderJobsSection(status: JailStatus): string {
  return status.availableJobs.map((job) => {
    const isActive = status.activeJob?.type === job.id;
    const isOtherActive = status.activeJob !== null && !isActive;
    const reward = formatJobReward(job);

    if (isActive && status.activeJob) {
      const aj = status.activeJob;
      const pct = aj.progressPercent.toFixed(0);
      return `
        <div class="jail-job-card jail-job-card--active" data-job-id="${esc(job.id)}">
          <div class="jail-job-info">
            <span class="jail-job-name">${esc(job.name)}</span>
            <span class="jail-job-reward">${reward}</span>
          </div>
          <div class="jail-job-progress">
            <div class="jail-job-progress-fill" style="width:${pct}%"></div>
          </div>
          <div class="jail-job-timer" id="jail-job-timer">
            ${formatCountdown(aj.completesAt)}
          </div>
          <button
            class="jail-btn jail-btn--complete"
            data-activity-id="${esc(aj.id)}"
            id="jail-btn-complete"
            type="button"
            style="display:none"
          >ABSCHLIESSEN</button>
        </div>
      `;
    }

    return `
      <div class="jail-job-card" data-job-id="${esc(job.id)}">
        <div class="jail-job-info">
          <span class="jail-job-name">${esc(job.name)}</span>
          <span class="jail-job-meta">${String(job.durationMinutes)} min</span>
          <span class="jail-job-reward">${reward}</span>
        </div>
        <button
          class="jail-btn jail-btn--start"
          data-job-id="${esc(job.id)}"
          type="button"
          ${isOtherActive ? 'disabled' : ''}
        >STARTEN</button>
      </div>
    `;
  }).join('');
}

function renderActivitiesSection(status: JailStatus): string {
  const cards = status.availableActivities.map((act) => {
    const cooldownUntil = status.cooldowns[act.id];
    const onCooldown = !!cooldownUntil;
    const cooldownLabel = onCooldown ? formatCooldownUntil(cooldownUntil) : `${String(act.cooldownHours)}h Cooldown`;
    const reward = formatActivityReward(act);

    // GERÜCHTE STREUEN: Warnung vor dem Button anzeigen
    const geruechteWarning = act.rewardGeruechteFlag
      ? `<span class="jail-activity-warning">WARNUNG: Dein Fahndungslevel sinkt bei Entlassung nur um 1 statt um 2.</span>`
      : '';

    return `
      <div class="jail-activity-card${onCooldown ? ' jail-activity-card--cooldown' : ''}">
        <div class="jail-activity-info">
          <span class="jail-activity-name">${esc(act.name)}</span>
          <span class="jail-activity-cd">${cooldownLabel}</span>
          <span class="jail-activity-reward">${reward}</span>
          ${act.riskFailChance ? `<span class="jail-activity-risk">${String(Math.round(act.riskFailChance * 100))}% Risiko: +${String(act.riskExtraJailMinutes ?? 0)}min</span>` : ''}
          ${geruechteWarning}
        </div>
        <button
          class="jail-btn jail-btn--execute"
          data-activity-id="${esc(act.id)}"
          type="button"
          ${onCooldown ? 'disabled' : ''}
        >AUSFÜHREN</button>
      </div>
    `;
  });

  return cards.join('');
}

function renderCellmateSection(): string {
  const cm = currentCellMate;

  if (!cm) {
    return `
      <div class="jail-activity-card">
        <div class="jail-activity-info">
          <span class="jail-activity-name">Zellengenosse kennenlernen</span>
          <span class="jail-activity-cd">Einmalig pro Knastaufenthalt</span>
          <span class="jail-activity-reward">Schmuggel-Aufträge nach Entlassung</span>
        </div>
        <button class="jail-btn jail-btn--execute" id="jail-btn-meet-cellmate" type="button">KONTAKT AUFNEHMEN</button>
      </div>
    `;
  }

  const trustLabel = cm.trustLevel >= 1
    ? '<span class="jail-activity-reward">VERTRAUEN AUFGEBAUT</span>'
    : '<span class="jail-activity-cd">Kein Vertrauen</span>';

  const job = cm.activeJob;
  let jobHtml = '';
  if (job && job.isTrustTest && !job.isCompleted && !job.isFailed) {
    if (job.completesAt && new Date() < new Date(job.completesAt)) {
      jobHtml = `
        <div class="jail-activity-card jail-activity-card--cooldown">
          <div class="jail-activity-info">
            <span class="jail-activity-name">VERTRAUENSTEST LÄUFT</span>
            <span class="jail-activity-cd" id="jail-cellmate-trust-timer">${formatCountdown(job.completesAt)}</span>
            <span class="jail-activity-reward">+${(Number(job.rewardCents) / 100).toFixed(0)} S-₦ bei Abschluss</span>
          </div>
          <button class="jail-btn" type="button" disabled>LÄUFT...</button>
        </div>
      `;
    } else {
      jobHtml = `
        <div class="jail-activity-card">
          <div class="jail-activity-info">
            <span class="jail-activity-name">VERTRAUENSTEST ABSCHLIESSEN</span>
            <span class="jail-activity-reward">+${(Number(job.rewardCents) / 100).toFixed(0)} S-₦</span>
          </div>
          <button class="jail-btn jail-btn--execute" id="jail-btn-complete-trust-test" data-job-id="${esc(job.id)}" type="button">ABSCHLIESSEN</button>
        </div>
      `;
    }
  } else if (cm.trustLevel === 0 && !job) {
    jobHtml = `
      <div class="jail-activity-card">
        <div class="jail-activity-info">
          <span class="jail-activity-name">Vertrauenstest starten</span>
          <span class="jail-activity-cd">15–45 min · Bargeld-Schmuggel</span>
          <span class="jail-activity-reward">Kleines S-₦ Entgelt</span>
        </div>
        <button class="jail-btn jail-btn--execute" id="jail-btn-start-trust-test" type="button">TEST STARTEN</button>
      </div>
    `;
  } else if (cm.trustLevel >= 1) {
    jobHtml = `<div class="jail-activity-card jail-activity-card--cooldown">
      <div class="jail-activity-info">
        <span class="jail-activity-name">Vertrauen bestätigt</span>
        <span class="jail-activity-reward">${esc(cm.npcName)} schickt dir nach der Entlassung Aufträge.</span>
      </div>
    </div>`;
  }

  return `
    <div class="jail-activity-card">
      <div class="jail-activity-info">
        <span class="jail-activity-name">${esc(cm.npcName)}</span>
        <span class="jail-activity-cd">Noch ${String(cm.remainingDays)} Tag(e)</span>
        ${trustLabel}
      </div>
    </div>
    ${jobHtml}
  `;
}

// ─── Event-Binding ───────────────────────────────────────────────────────────

function bindJailEvents(status: JailStatus): void {
  const app = document.getElementById('app');
  if (!app) return;

  // Kaution
  document.getElementById('jail-btn-bail')?.addEventListener('click', () => {
    void handleBail();
  });

  // Knast-Job starten
  app.querySelectorAll<HTMLButtonElement>('.jail-btn--start').forEach((btn) => {
    btn.addEventListener('click', () => {
      const jobId = btn.dataset.jobId;
      if (jobId) void handleStartJob(jobId);
    });
  });

  // Knast-Job abschließen
  document.getElementById('jail-btn-complete')?.addEventListener('click', () => {
    const actId = (document.getElementById('jail-btn-complete') as HTMLButtonElement | null)?.dataset.activityId;
    if (actId) void handleCompleteJob(actId);
  });

  // Aktivitäten ausführen (ohne Zellengenosse-Buttons)
  app.querySelectorAll<HTMLButtonElement>('.jail-btn--execute:not(#jail-btn-meet-cellmate):not(#jail-btn-start-trust-test):not(#jail-btn-complete-trust-test)').forEach((btn) => {
    btn.addEventListener('click', () => {
      const actId = btn.dataset.activityId;
      if (actId) void handleExecuteActivity(actId);
    });
  });

  // Zellengenosse kennenlernen
  document.getElementById('jail-btn-meet-cellmate')?.addEventListener('click', () => {
    void handleMeetCellmate();
  });

  // Vertrauenstest starten
  document.getElementById('jail-btn-start-trust-test')?.addEventListener('click', () => {
    void handleStartTrustTest();
  });

  // Vertrauenstest abschließen
  document.getElementById('jail-btn-complete-trust-test')?.addEventListener('click', () => {
    void handleCompleteTrustTest();
  });

  void status;
}

// ─── API-Aktionen ────────────────────────────────────────────────────────────

async function handleBail(): Promise<void> {
  const btn = document.getElementById('jail-btn-bail') as HTMLButtonElement | null;
  if (btn) btn.disabled = true;

  try {
    const res = await api.post<{ data: { bailCostScheine: number; jailReport?: JailReport; message: string } }>('/police/bail', {});
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    const data = (res as unknown as { data: { bailCostScheine: number; jailReport?: JailReport } }).data;
    void refreshBalance();
    const player = gameState.getPlayer();
    if (player) gameState.setPlayer({ ...player, isInJail: false, jailReleasesAt: null });
    stopAllTimers();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    if (data?.jailReport) {
      showJailReport(data.jailReport, () => { navigate('/dashboard'); });
    } else {
      navigate('/dashboard');
    }
  } catch (err: unknown) {
    if (btn) btn.disabled = false;
    showJailError(err);
  }
}

async function handleStartJob(jobId: string): Promise<void> {
  try {
    await api.post('/jail/job/start', { jobId });
    await refreshStatus();
  } catch (err: unknown) {
    showJailError(err);
  }
}

async function handleCompleteJob(activityId: string): Promise<void> {
  try {
    const result = await api.post<{ data: { reward: Record<string, unknown>; message: string } }>(
      '/jail/job/complete',
      { activityId },
    );
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- s.o.
    const data = (result as unknown as { data: { reward: Record<string, unknown>; message: string } }).data;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    showJailMessage(`Abgeschlossen: ${formatJobRewardOutcome(data?.reward ?? {})}`);
    await refreshStatus();
  } catch (err: unknown) {
    showJailError(err);
  }
}

async function handleExecuteActivity(activityId: string): Promise<void> {
  try {
    const result = await api.post<{ data: { outcome: Record<string, unknown>; cooldownUntil: string } }>(
      '/jail/activity/execute',
      { activityId },
    );
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    const data = (result as unknown as { data: { outcome: Record<string, unknown> } }).data;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    showJailMessage(formatActivityOutcome(data?.outcome ?? {}));
    await refreshStatus();
  } catch (err: unknown) {
    showJailError(err);
  }
}

async function handleMeetCellmate(): Promise<void> {
  try {
    const res = await api.post<{ data: CellMateView }>('/jail/cellmate/meet', {});
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    currentCellMate = (res as unknown as { data: CellMateView }).data ?? null;
    const section = document.getElementById('jail-cellmate-section');
    if (section) {
      section.innerHTML = `<div class="jail-section-title">ZELLENGENOSSE</div>${renderCellmateSection()}`;
      bindCellmateEvents();
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    showJailMessage(`Zellengenosse ${currentCellMate?.npcName ?? ''} kennen gelernt.`);
  } catch (err: unknown) {
    showJailError(err);
  }
}

async function handleStartTrustTest(): Promise<void> {
  try {
    const res = await api.post<{ data: CellMateView }>('/jail/cellmate/trust-test', {});
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    currentCellMate = (res as unknown as { data: CellMateView }).data ?? null;
    const section = document.getElementById('jail-cellmate-section');
    if (section) {
      section.innerHTML = `<div class="jail-section-title">ZELLENGENOSSE</div>${renderCellmateSection()}`;
      bindCellmateEvents();
    }
    showJailMessage('Vertrauenstest gestartet.');
  } catch (err: unknown) {
    showJailError(err);
  }
}

async function handleCompleteTrustTest(): Promise<void> {
  try {
    const res = await api.post<{ data: { cellMate: CellMateView; rewardCents: string } }>('/jail/cellmate/trust-test/complete', {});
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- s.o.
    const data = (res as unknown as { data: { cellMate: CellMateView; rewardCents: string } }).data;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    currentCellMate = data?.cellMate ?? null;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- s.o.
    const reward = data?.rewardCents ? `+${(Number(data.rewardCents) / 100).toFixed(0)} S-₦` : '';
    const section = document.getElementById('jail-cellmate-section');
    if (section) {
      section.innerHTML = `<div class="jail-section-title">ZELLENGENOSSE</div>${renderCellmateSection()}`;
      bindCellmateEvents();
    }
    void refreshBalance();
    showJailMessage(`Vertrauenstest abgeschlossen. ${reward}`);
  } catch (err: unknown) {
    showJailError(err);
  }
}

function bindCellmateEvents(): void {
  document.getElementById('jail-btn-meet-cellmate')?.addEventListener('click', () => {
    void handleMeetCellmate();
  });
  document.getElementById('jail-btn-start-trust-test')?.addEventListener('click', () => {
    void handleStartTrustTest();
  });
  document.getElementById('jail-btn-complete-trust-test')?.addEventListener('click', () => {
    void handleCompleteTrustTest();
  });
}

/** Ausgabe eines Job-Abschluss-Rewards lesbar machen */
function formatJobRewardOutcome(reward: Record<string, unknown>): string {
  const parts: string[] = [];
  if (reward.eurCents) parts.push(`+${(Number(reward.eurCents) / 100).toFixed(0)}\u20a6`);
  if (typeof reward.xp === 'number') parts.push(`+${reward.xp} XP`);
  return parts.join(', ') || 'Abgeschlossen.';
}

/** Aktivitaets-Ergebnis in lesbarer Form ausgeben */
function formatActivityOutcome(outcome: Record<string, unknown>): string {
  if (outcome.success === false) {
    return typeof outcome.message === 'string' && outcome.message.length > 0
      ? outcome.message
      : 'Gescheitert – Haftzeit verlängert!';
  }
  const parts: string[] = [];
  if (outcome.eurCents) parts.push(`+${(Number(outcome.eurCents) / 100).toFixed(0)}\u20a6`);
  if (outcome.blackMoneyCents) parts.push(`+${(Number(outcome.blackMoneyCents) / 100).toFixed(0)} S-\u20a6`);
  if (typeof outcome.wantedPointsRemoved === 'number') parts.push(`-${outcome.wantedPointsRemoved} Fahndungspunkte`);
  if (outcome.geruechteActive) parts.push('Ger\u00fcchte aktiv \u2013 FL bei Entlassung wird h\u00f6her sein.');
  if (typeof outcome.contact === 'string') {
    const contactNames: Record<string, string> = {
      'kontakt_gruener_guenther': 'Der Gärtner',
      'kontakt_alchemist': 'Der Alchemist',
    };
    const name = contactNames[outcome.contact] ?? 'Unbekannter Kontakt';
    parts.push(`Kontakt geknüpft: ${name}! Erscheint im Markt.`);
  } else if (outcome.contact === null && outcome.eurCents) {
    parts.push('(Alle Kontakte bekannt – Bonus erhalten)');
  }
  return parts.join(' · ') || 'Aktivität erfolgreich.';
}

// ─── Timer-Logik ─────────────────────────────────────────────────────────────

function startTimers(status: JailStatus): void {
  // Absoluten Server-Timestamp verwenden, damit kein Drift beim Status-Refresh entsteht
  if (status.remainingMinutes > 0) {
    jailReleaseTime = status.jailReleasesAt
      ? new Date(status.jailReleasesAt)
      : new Date(Date.now() + status.remainingMinutes * 60 * 1000);
    jailCountdownInterval = setInterval(updateMainTimer, 1000);
  }

  // Job-Countdown
  if (status.activeJob) {
    jobCompleteTime = new Date(status.activeJob.completesAt);
    jailJobCountdownInterval = setInterval(updateJobTimer, 1000);
  }

  // Status-Polling alle 30s
  statusPollInterval = setInterval(() => {
    void refreshStatus();
  }, 30_000);
}

function stopAllTimers(): void {
  if (statusPollInterval) { clearInterval(statusPollInterval); statusPollInterval = null; }
  if (jailCountdownInterval) { clearInterval(jailCountdownInterval); jailCountdownInterval = null; }
  if (jailJobCountdownInterval) { clearInterval(jailJobCountdownInterval); jailJobCountdownInterval = null; }
}

function updateMainTimer(): void {
  if (!jailReleaseTime) return;
  const remainingMs = jailReleaseTime.getTime() - Date.now();
  const timerEl = document.getElementById('jail-main-timer');
  if (!timerEl) return;

  if (remainingMs <= 0) {
    timerEl.textContent = '00:00';
    timerEl.classList.add('jail-timer--danger');
    void refreshStatus();
    return;
  }

  const mins = Math.floor(remainingMs / 60000);
  const secs = Math.floor((remainingMs % 60000) / 1000);
  timerEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  if (mins < 5) timerEl.classList.add('jail-timer--danger');
}

function updateJobTimer(): void {
  if (!jobCompleteTime) return;
  const remainingMs = jobCompleteTime.getTime() - Date.now();
  const timerEl = document.getElementById('jail-job-timer');
  const completeBtn = document.getElementById('jail-btn-complete') as HTMLButtonElement | null;

  if (!timerEl) return;

  if (remainingMs <= 0) {
    timerEl.textContent = 'FERTIG';
    if (completeBtn) completeBtn.style.display = '';
    if (jailJobCountdownInterval) { clearInterval(jailJobCountdownInterval); jailJobCountdownInterval = null; }
    return;
  }

  const mins = Math.floor(remainingMs / 60000);
  const secs = Math.floor((remainingMs % 60000) / 1000);
  timerEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

async function refreshStatus(): Promise<void> {
  try {
    const res = await api.get<{ data: JailStatus }>('/jail/status');
    // Doppel-Cast bewusst, s.o. (Zeile ~140).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unnecessary-condition -- s.o.
    const status = (res as unknown as { data: JailStatus }).data ?? (res as unknown as JailStatus);

    if (!status.isInJail) {
      stopAllTimers();
      const player = gameState.getPlayer();
      if (player) gameState.setPlayer({ ...player, isInJail: false, jailReleasesAt: null });
      if (status.jailReport) {
        showJailReport(status.jailReport, () => { navigate('/dashboard'); });
      } else {
        navigate('/dashboard');
      }
      return;
    }

    currentStatus = status;

    // Zellengenosse-Status parallel laden
    try {
      const cmRes = await api.get<{ data: CellMateView | null }>('/jail/cellmate');
      // Doppel-Cast bewusst, s.o. (Zeile ~140).
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- s.o.
      currentCellMate = (cmRes as unknown as { data: CellMateView | null }).data ?? null;
    } catch {
      // Stille Fehler – currentCellMate bleibt unverändert
    }

    stopAllTimers();

    const app = document.getElementById('app');
    if (app) {
      renderJail(app, status);
      startTimers(status);
    }
  } catch {
    // Stille Fehler beim Poll
  }
}

// ─── Ausgegraute Nav-Sidebar für Knast-Modus ─────────────────────────────────

function renderLockedNav(): string {
  const groups: { label: string; items: string[] }[] = [
    { label: 'KARRIERE',   items: ['JOBS', 'TALENTBAUM', 'PROFIL'] },
    { label: 'BUSINESS',   items: ['MARKT', 'LAGERHAUS', 'CRIME OPS'] },
    { label: 'IMMOBILIEN', items: ['GESCHÄFTE', 'LAGER', 'VERSTECKE'] },
    { label: 'COMMUNITY',  items: ['LEADERBOARD'] },
  ];
  return groups.map((g) => `
    <div class="dash-nav-group">
      <p class="dash-nav-group-label">${g.label}</p>
      ${g.items.map((item) => `
        <span class="dash-nav-item jail-nav-item-locked">${item}</span>
      `).join('')}
    </div>
  `).join('');
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${String(h)}h ${String(m).padStart(2, '0')}m`;
  return `${String(minutes).padStart(2, '0')}:00`;
}

function formatCountdown(completesAt: string): string {
  const remainingMs = new Date(completesAt).getTime() - Date.now();
  if (remainingMs <= 0) return 'FERTIG';
  const mins = Math.floor(remainingMs / 60000);
  const secs = Math.floor((remainingMs % 60000) / 1000);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function formatCooldownUntil(isoString: string): string {
  const d = new Date(isoString);
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `CD BIS ${h}:${m}`;
}

function formatJobReward(job: JailJobDef): string {
  if (job.rewardXpPerMinute) return `+${String(job.rewardXpPerMinute * job.durationMinutes)} XP`;
  if (job.rewardEurCentsMin !== undefined && job.rewardEurCentsMax !== undefined) {
    const min = (Number(job.rewardEurCentsMin) / 100).toFixed(0);
    const max = (Number(job.rewardEurCentsMax) / 100).toFixed(0);
    return `${min}&ndash;${max}&nbsp;&#8358;`;
  }
  if (job.rewardEurCents) return `${(Number(job.rewardEurCents) / 100).toFixed(0)}&nbsp;&#8358;`;
  return '';
}

function formatActivityReward(act: JailActivityDef): string {
  const parts: string[] = [];
  if (act.rewardEurCents && Number(act.rewardEurCents) > 0) parts.push(`${(Number(act.rewardEurCents) / 100).toFixed(0)}&nbsp;&#8358;`);
  if (act.rewardBlackMoneyCents && Number(act.rewardBlackMoneyCents) > 0) parts.push(`+${(Number(act.rewardBlackMoneyCents) / 100).toFixed(0)}&nbsp;S-&#8358;`);
  if (act.rewardWantedPointsRemoved) parts.push(`-${String(act.rewardWantedPointsRemoved)} FL-Punkte`);
  if (act.rewardContact) parts.push('NPC-Kontakt');
  return parts.join(' + ') || '';
}

function showJailReport(report: JailReport, onClose: () => void): void {
  const existing = document.getElementById('jail-report-overlay');
  if (existing) existing.remove();

  const CONTACT_NAMES: Record<string, string> = {
    'kontakt_gruener_guenther': 'Der Gärtner',
    'kontakt_alchemist': 'Der Alchemist',
  };

  const hasContent = report.completedJobs.length > 0 || report.completedActivities.length > 0;
  const totalNox = (Number(report.totalNoxCents) / 100).toFixed(2);

  const jobsHtml = report.completedJobs.map((j) => {
    const parts: string[] = [];
    if (j.reward.eurCents) parts.push(`+${(Number(j.reward.eurCents) / 100).toFixed(0)}₦`);
    if (j.reward.xp) parts.push(`+${String(j.reward.xp)} XP`);
    return `<div class="jr-row"><span class="jr-label">${esc(j.name)}</span><span class="jr-value jr-green">${parts.join(' / ') || '–'}</span></div>`;
  }).join('');

  const activitiesHtml = report.completedActivities.map((a) => {
    if (!a.success) {
      return `<div class="jr-row"><span class="jr-label">${esc(a.name)}</span><span class="jr-value jr-red">AUFGEFLOGEN</span></div>`;
    }
    const parts: string[] = [];
    if (a.outcome.eurCents) parts.push(`+${(Number(a.outcome.eurCents) / 100).toFixed(0)}₦`);
    if (a.outcome.wantedPointsRemoved) parts.push(`-${String(a.outcome.wantedPointsRemoved)} FL-Pkt.`);
    if (a.outcome.contact) parts.push(CONTACT_NAMES[a.outcome.contact] ?? a.outcome.contact);
    return `<div class="jr-row"><span class="jr-label">${esc(a.name)}</span><span class="jr-value jr-green">${parts.join(' / ') || 'OK'}</span></div>`;
  }).join('');

  const overlay = document.createElement('div');
  overlay.id = 'jail-report-overlay';
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal jr-modal" role="dialog" aria-modal="true" aria-label="Entlassungsbericht">
      <div class="modal-header"><h2>ENTLASSUNGSBERICHT</h2></div>
      <div class="modal-body">
        ${hasContent ? `
          ${report.completedJobs.length > 0 ? `
            <div class="jr-section-title">TATIGKEITEN</div>
            ${jobsHtml}
          ` : ''}
          ${report.completedActivities.length > 0 ? `
            <div class="jr-section-title">AKTIONEN</div>
            ${activitiesHtml}
          ` : ''}
          <div class="jr-divider"></div>
          <div class="jr-row jr-total">
            <span class="jr-label">GESAMT VERDIENT</span>
            <span class="jr-value jr-gold">${totalNox}₦${report.totalXp > 0 ? ` / +${String(report.totalXp)} XP` : ''}</span>
          </div>
          ${report.contactsUnlocked.length > 0 ? `
            <div class="jr-row">
              <span class="jr-label">KONTAKT GEKNÜPFT</span>
              <span class="jr-value jr-purple">${report.contactsUnlocked.map((c) => CONTACT_NAMES[c] ?? c).join(', ')}</span>
            </div>
          ` : ''}
        ` : '<div class="jr-empty">Keine Aktivitäten während der Haft.</div>'}
      </div>
      <div class="modal-actions">
        <button class="jr-close-btn" type="button">ENTLASSEN – WEITER</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  overlay.querySelector('.jr-close-btn')?.addEventListener('click', () => {
    overlay.remove();
    onClose();
  });
}

function showJailError(err: unknown): void {
  const msg = (err instanceof Error) ? err.message : 'Unbekannter Fehler.';
  showJailMessage(`FEHLER: ${msg}`, true);
}

function showJailMessage(text: string, isError = false): void {
  const existing = document.getElementById('jail-feedback');
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.id = 'jail-feedback';
  el.style.cssText = `
    position:fixed;bottom:80px;left:50%;transform:translateX(-50%);
    background:var(--bg-elevated);border:1px solid ${isError ? 'var(--accent-red)' : 'var(--border)'};
    color:${isError ? 'var(--accent-red)' : 'var(--text-primary)'};
    font-family:var(--font-mono);font-size:0.7rem;padding:8px 14px;z-index:1000;
  `;
  el.textContent = text;
  el.addEventListener('click', () => { el.remove(); });
  document.body.appendChild(el);
  setTimeout(() => { el.remove(); }, calcNoticeDisplayMs(text, isError));
}

void currentStatus;

// ─── Styles ──────────────────────────────────────────────────────────────────

function injectStyles(): void {
  const styleId = 'jail-page-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Jail-Layout: nutzt Dashboard-Shell, Sidebar ausgegraut */
    .jail-nav-locked {
      opacity: 0.35;
      pointer-events: none;
      user-select: none;
      filter: grayscale(0.5);
    }
    .jail-nav-item-locked {
      display: block;
      padding: 6px 8px;
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      letter-spacing: 0.06em;
      cursor: default;
    }
    .jail-main {
      overflow-y: auto;
      height: 100%;
    }
    .jail-page {
      padding: 16px;
      max-width: 600px;
      margin: 0 auto;
      font-family: var(--font-mono);
    }
    .jail-loading, .jail-error {
      padding: 2rem;
      font-family: var(--font-mono);
      color: var(--text-muted);
      text-align: center;
    }
    .jail-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid var(--accent-red);
      padding-bottom: 10px;
      margin-bottom: 14px;
    }
    .jail-title {
      font-size: 1rem;
      color: var(--accent-red);
      letter-spacing: 0.15em;
      text-transform: uppercase;
    }
    .jail-timer {
      font-size: 1.2rem;
      color: var(--text-primary);
      letter-spacing: 0.1em;
    }
    .jail-timer--danger { color: var(--accent-red); }

    .jail-status-bar {
      margin-bottom: 18px;
    }
    .jail-flavour {
      color: var(--text-italic);
      font-size: 0.75rem;
      font-style: italic;
      margin-bottom: 10px;
    }
    .jail-actions-top {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .jail-scheine-hint {
      font-size: 0.65rem;
      color: var(--text-muted);
    }

    .jail-section { margin-bottom: 20px; }
    .jail-section-title {
      font-size: 0.65rem;
      color: var(--text-muted);
      letter-spacing: 0.12em;
      text-transform: uppercase;
      border-bottom: 1px solid var(--border);
      padding-bottom: 4px;
      margin-bottom: 8px;
    }

    .jail-job-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid var(--border);
      gap: 10px;
      flex-wrap: wrap;
    }
    .jail-job-card--active { border-color: var(--accent-orange); }
    .jail-job-info { display: flex; gap: 10px; align-items: baseline; flex: 1; }
    .jail-job-name { font-size: 0.8rem; color: var(--text-primary); text-transform: uppercase; }
    .jail-job-meta { font-size: 0.65rem; color: var(--text-muted); }
    .jail-job-reward { font-size: 0.7rem; color: var(--accent-green); }
    .jail-job-progress {
      width: 100%;
      height: 3px;
      background: var(--border);
      margin-top: 4px;
    }
    .jail-job-progress-fill { height: 100%; background: var(--accent-orange); transition: width 0.5s; }
    .jail-job-timer { font-size: 0.7rem; color: var(--text-muted); }

    .jail-activity-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid var(--border);
      gap: 10px;
      flex-wrap: wrap;
    }
    .jail-activity-card--cooldown { opacity: 0.5; }
    .jail-activity-info { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; flex: 1; }
    .jail-activity-name { font-size: 0.8rem; color: var(--text-primary); text-transform: uppercase; }
    .jail-activity-cd { font-size: 0.65rem; color: var(--text-muted); }
    .jail-activity-reward { font-size: 0.7rem; color: var(--accent-green); }
    .jail-activity-risk { font-size: 0.65rem; color: var(--accent-orange); }

    .jail-btn {
      font-family: var(--font-mono);
      font-size: 0.68rem;
      letter-spacing: 0.08em;
      padding: 6px 10px;
      border: 1px solid var(--border);
      background: var(--bg-surface);
      color: var(--text-primary);
      text-transform: uppercase;
      cursor: pointer;
      white-space: nowrap;
    }
    .jail-btn:hover:not(:disabled) { border-color: var(--text-primary); background: var(--bg-elevated); }
    .jail-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .jail-btn--bail { border-color: var(--accent-gold); color: var(--accent-gold); }
    .jail-btn--bail:hover:not(:disabled) { background: rgba(240,180,41,0.08); }
    .jail-btn--start { border-color: var(--accent-green); color: var(--accent-green); }
    .jail-btn--start:hover:not(:disabled) { background: rgba(82,183,136,0.08); }
    .jail-btn--complete { border-color: var(--accent-blue); color: var(--accent-blue); }
    .jail-btn--execute { border-color: var(--accent-purple); color: var(--accent-purple); }
    .jail-btn--execute:hover:not(:disabled) { background: rgba(155,114,207,0.08); }

    /* Entlassungsbericht-Modal – Shell = .modal-backdrop/.modal aus dashboard.css
       (siehe components/BottomSheet.ts). .jr-modal ist nur die Gold-Akzent-Variante. */
    .jr-modal { border-color: var(--accent-gold); }
    .jr-modal .modal-header { border-bottom-color: var(--accent-gold); }
    .jr-modal .modal-header h2 { color: var(--accent-gold); }
    .jr-section-title {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      letter-spacing: 0.1em;
      margin: 10px 0 4px;
    }
    .jr-row {
      display: flex; justify-content: space-between; align-items: baseline;
      padding: 4px 0;
      font-size: var(--font-size-xs);
      border-bottom: 1px solid var(--border);
      gap: 12px;
    }
    .jr-total { padding: 8px 0; }
    .jr-label { color: var(--text-primary); flex: 1; }
    .jr-value { text-align: right; white-space: nowrap; }
    .jr-green { color: var(--accent-green); }
    .jr-red { color: var(--accent-red); }
    .jr-gold { color: var(--accent-gold); font-size: var(--font-size-sm); }
    .jr-purple { color: var(--accent-purple); }
    .jr-divider { border-bottom: 1px solid var(--border); margin: 8px 0; }
    .jr-empty {
      color: var(--text-muted); font-size: var(--font-size-xs);
      font-style: italic; padding: 10px 0;
    }
    .jr-close-btn {
      display: block; width: 100%;
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      letter-spacing: 0.1em; padding: 8px;
      border: 1px solid var(--accent-gold); background: transparent;
      color: var(--accent-gold); cursor: pointer; text-transform: uppercase;
    }
    .jr-close-btn:hover { background: rgba(240,180,41,0.08); }
  `;
  document.head.appendChild(style);
}
